import { useState, useMemo, useEffect } from "react";
import { X, Filter } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

const fallbackGallery = [
  // Events
  { src: "/events/event-golf.jpg", caption: "Recovery Connections Golf Fundraiser", date: "September 12, 2021", category: "events", event: "Recovery Connections Golf Fundraiser" },
  { src: "/events/event-apald.jpg", caption: "APALD Rally", date: "June 4, 2021", category: "events", event: "APALD Rally" },
  { src: "/events/event-rally.jpg", caption: "APALD Rally", date: "June 4, 2021", category: "events", event: "APALD Rally" },
  { src: "/events/event-jcc.jpg", caption: "JCC Panel Discussion", date: "May 27, 2021", category: "events", event: "JCC Panel Discussion" },

  // Awareness / community
  { src: "/events/value-education.jpg", caption: "Education in the community", date: "", category: "awareness", event: "Community Awareness" },
  { src: "/events/value-awareness.jpg", caption: "Awareness in action", date: "", category: "awareness", event: "Community Awareness" },
  { src: "/events/value-support.jpg", caption: "Supporting our community", date: "", category: "awareness", event: "Community Awareness" },
  { src: "/events/vision.jpg", caption: "Finding peace in our grief", date: "", category: "awareness", event: "Community Awareness" },
  { src: "/mission-photo.jpg", caption: "Changing minds, changing lives", date: "", category: "awareness", event: "Community Awareness" },
  { src: "/license-plate.jpg", caption: "Florida Overdose Awareness license plate", date: "Approved 2025", category: "awareness", event: "License Plate Campaign" },

  // In loving memory
  { src: "/board/tylar-ann.jpg", caption: "In loving memory of Tylar-Ann", date: "1989 - 2015", category: "memory", event: "In Loving Memory" },
  { src: "/board/anna.jpg", caption: "In loving memory of Anna", date: "", category: "memory", event: "In Loving Memory" },
  { src: "/board/andrew.jpg", caption: "In loving memory of Andrew", date: "", category: "memory", event: "In Loving Memory" },
  { src: "/board/lynda.jpg", caption: "In loving memory of Lynda", date: "", category: "memory", event: "In Loving Memory" },
  { src: "/board/rachel.jpg", caption: "In loving memory of Rachel Amber - Forever 33", date: "1986 - 2019", category: "memory", event: "In Loving Memory" },
];

const filters = [
  { key: "all", label: "All Photos" },
  { key: "events", label: "Events" },
  { key: "awareness", label: "Awareness" },
  { key: "memory", label: "In Loving Memory" },
];

export default function Gallery() {
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState(fallbackGallery);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/gallery");
        if (Array.isArray(data) && data.length > 0) {
          // Normalize API shape to gallery item shape (map image_path -> src)
          setItems(data.map((g) => ({
            src: fileUrl(g.image_path),
            caption: g.caption || "",
            date: g.date || "",
            category: g.category || "events",
            event: g.event_name || g.caption || "",
          })));
        }
      } catch {}
    })();
  }, []);

  const galleryItems = items;
  const filtered = useMemo(
    () => (filter === "all" ? galleryItems : galleryItems.filter((i) => i.category === filter)),
    [filter, galleryItems]
  );

  // For Events tab, group by event name
  const groupedByEvent = useMemo(() => {
    if (filter !== "events") return null;
    const map = new Map();
    filtered.forEach((i) => {
      const arr = map.get(i.event) || [];
      arr.push(i);
      map.set(i.event, arr);
    });
    return Array.from(map.entries());
  }, [filter, filtered]);
  return (
    <div data-testid="gallery-page">
      <section className="bg-[var(--rts-blue-pale)] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-red)] mb-4">
            Photo Gallery
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--rts-ink)] leading-[1.05]">
            Moments that <span className="italic text-[var(--rts-blue-deep)]">moved us forward.</span>
          </h1>
          <p className="mt-5 text-lg text-[var(--rts-ink-soft)] max-w-3xl leading-relaxed">
            Events, rallies, family photos, and quiet moments of remembrance - the stories that make PARS.
          </p>
        </div>
      </section>

      {/* Filter bar */}
      <section className="bg-white sticky top-20 z-30 border-b border-[var(--rts-blue-pale)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3 overflow-x-auto">
          <Filter className="w-4 h-4 text-[var(--rts-muted)] shrink-0" aria-hidden="true" />
          <div className="flex gap-2" role="tablist" aria-label="Gallery filters" data-testid="gallery-filters">
            {filters.map((f) => (
              <button
                key={f.key}
                role="tab"
                aria-selected={filter === f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`gallery-filter-${f.key}`}
                className={`px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all min-h-[44px] ${
                  filter === f.key
                    ? "bg-[var(--rts-blue-deep)] text-white shadow-md"
                    : "bg-[var(--rts-blue-pale)] text-[var(--rts-ink)] hover:bg-[var(--rts-blue)] hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-6">
          {groupedByEvent ? (
            <div className="space-y-14">
              {groupedByEvent.map(([eventName, items]) => (
                <div key={eventName} data-testid={`gallery-event-group-${eventName.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex items-baseline justify-between mb-5 flex-wrap gap-2">
                    <h2 className="font-display text-2xl sm:text-3xl text-[var(--rts-ink)]">{eventName}</h2>
                    {items[0].date && (
                      <span className="text-sm text-[var(--rts-muted)] font-semibold">{items[0].date}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {items.map((item, i) => (
                      <Tile key={i} item={item} index={i} onClick={setActive} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map((item, i) => (
                <Tile key={i} item={item} index={i} onClick={setActive} />
              ))}
            </div>
          )}
        </div>
      </section>

      {active && (
        <div
          className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setActive(null)}
          role="dialog"
          aria-label="Image viewer"
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setActive(null)}
            aria-label="Close"
            data-testid="gallery-close"
            className="absolute top-5 right-5 w-12 h-12 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white"
          >
            <X className="w-6 h-6" />
          </button>
          <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={active.src} alt={active.caption} className="w-full max-h-[80vh] object-contain rounded-xl" />
            <figcaption className="text-center text-white mt-4">
              <p className="font-display text-lg">{active.caption}</p>
              {active.date && <p className="text-sm text-white/65 mt-1">{active.date}</p>}
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}

function Tile({ item, index, onClick }) {
  return (
    <button
      onClick={() => onClick(item)}
      data-testid={`gallery-tile-${index}`}
      className="group relative overflow-hidden rounded-2xl aspect-square bg-[var(--rts-blue-pale)] focus:outline-none focus:ring-4 focus:ring-[var(--rts-blue-light)]"
    >
      <img
        src={item.src}
        alt={item.caption}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
        <div className="text-left text-white">
          <p className="text-sm font-semibold leading-tight">{item.caption}</p>
          {item.date && <p className="text-xs text-white/75 mt-0.5">{item.date}</p>}
        </div>
      </div>
    </button>
  );
}
